import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useGuestGate } from "../lib/useGuestGate";
import PortfolioSection from "../components/PortfolioSection";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, TrendingDown, TrendingUp, Repeat } from "lucide-react";
import { toast } from "sonner";

const STRATEGY_LABEL = {
  sell_puts: { label: "Sell Puts", icon: TrendingDown, className: "bg-blue-50 text-blue-700 border-blue-200" },
  sell_covered_calls: { label: "Sell Covered Calls", icon: TrendingUp, className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  both: { label: "Both", icon: Repeat, className: "bg-violet-50 text-violet-700 border-violet-200" },
};

function StrategyBadge({ strategy }) {
  const cfg = STRATEGY_LABEL[strategy];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border ${cfg.className}`}
      data-testid={`strategy-badge-${strategy}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {cfg.label}
    </span>
  );
}

export default function StocksInPlayPage() {
  const { gate } = useGuestGate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = add mode
  const [ticker, setTicker] = useState("");
  const [strategy, setStrategy] = useState("sell_puts");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stocks");
      setStocks(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load stocks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setTicker("");
    setStrategy("sell_puts");
    setNotes("");
  };

  const openEdit = (s) => {
    if (gate("Editing a stock")) return;
    setEditingId(s.id);
    setTicker(s.ticker);
    setStrategy(s.strategy);
    setNotes(s.notes || "");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ticker.trim()) return;
    setSubmitting(true);
    const body = {
      ticker: ticker.trim().toUpperCase(),
      strategy,
      notes,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/stocks/${editingId}`, body);
        setStocks((prev) => prev.map((s) => (s.id === editingId ? data : s)));
        toast.success(`${data.ticker} updated`);
      } else {
        const { data } = await api.post("/stocks", body);
        setStocks((prev) => [data, ...prev]);
        toast.success(`${data.ticker} added`);
      }
      setOpen(false);
      resetForm();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          (editingId ? "Failed to update stock" : "Failed to add stock"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, tk) => {
    if (gate("Removing a stock")) return;
    try {
      await api.delete(`/stocks/${id}`);
      setStocks((prev) => prev.filter((s) => s.id !== id));
      toast.success(`${tk} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete stock");
    }
  };

  return (
    <div className="space-y-12" data-testid="stocks-in-play-page">
      <section className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="max-w-xl">
          <div className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-1">
            Daily Plays
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 font-heading">
            Stocks in Play
          </h2>
          <p className="text-slate-600 text-sm leading-relaxed mt-1">
            Curated tickers your team is actively trading today. Mark each with the chosen options strategy.
          </p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(o) => {
            if (o && gate("Adding a stock")) return;
            setOpen(o);
            if (!o) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
              onClick={() => setEditingId(null)}
              data-testid="add-stock-button"
            >
              <Plus className="w-4 h-4" />
              Add Stock
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="add-stock-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">
                {editingId ? "Edit Stock in Play" : "Add Stock in Play"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="ticker" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                  Ticker
                </Label>
                <Input
                  id="ticker"
                  required
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase())}
                  className="mt-1.5 font-mono uppercase"
                  placeholder="AAPL"
                  maxLength={10}
                  data-testid="ticker-input"
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-slate-600">Strategy</Label>
                <Select value={strategy} onValueChange={setStrategy}>
                  <SelectTrigger className="mt-1.5" data-testid="strategy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sell_puts" data-testid="strategy-option-sell-puts">Sell Puts</SelectItem>
                    <SelectItem value="sell_covered_calls" data-testid="strategy-option-sell-covered-calls">Sell Covered Calls</SelectItem>
                    <SelectItem value="both" data-testid="strategy-option-both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="notes" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                  Notes (optional)
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1.5"
                  rows={3}
                  placeholder="Strike levels, IV, catalysts..."
                  data-testid="notes-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setOpen(false); resetForm(); }}
                  data-testid="cancel-add-stock-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="submit-add-stock-button"
                >
                  {submitting
                    ? "Saving..."
                    : editingId ? "Save changes" : "Add Stock"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="stocks-table">
        <table className="w-full min-w-[640px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ticker</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Strategy</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Notes</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Added By</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Date</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[140px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-16 text-center text-sm text-slate-500" data-testid="stocks-loading">
                  Loading stocks...
                </td>
              </tr>
            ) : stocks.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center" data-testid="empty-stocks">
                  <div className="text-sm text-slate-500">No stocks in play yet.</div>
                  <div className="text-xs text-slate-400 mt-1">Click "Add Stock" to get started.</div>
                </td>
              </tr>
            ) : (
              stocks.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`stock-row-${s.ticker}`}>
                  <td className="py-4 px-6">
                    <span className="font-mono font-semibold text-slate-900 text-base tracking-wide">{s.ticker}</span>
                  </td>
                  <td className="py-4 px-6">
                    <StrategyBadge strategy={s.strategy} />
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-700 max-w-xs truncate hidden md:table-cell">
                    {s.notes || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-600 hidden lg:table-cell">{s.created_by_email}</td>
                  <td className="py-4 px-6 text-sm text-slate-500 hidden sm:table-cell">
                    {new Date(s.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-6 text-right w-[140px]">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => openEdit(s)}
                        aria-label={`Edit ${s.ticker}`}
                        title={`Edit ${s.ticker}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                        data-testid={`edit-stock-${s.id}`}
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(s.id, s.ticker)}
                        aria-label={`Remove ${s.ticker}`}
                        title={`Remove ${s.ticker}`}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        data-testid={`delete-stock-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </section>

      <div className="border-t border-slate-200" />

      <PortfolioSection />
    </div>
  );
}
